<script lang="ts" setup>
const props = defineProps<{
  title: string;
  subtitle?: string;
  backgroundImage?: string;
}>();

const placeholderReady = ref(false);
const fullReady = ref(false);

function getPlaceholderUrl(url: string): string {
  // Insert a tiny blurred Cloudinary transformation before the version segment
  return url.replace(/\/upload\/.*?(v\d+\/)/, '/upload/w_30,q_10,e_blur:500/$1');
}

onMounted(() => {
  if (!props.backgroundImage) return;

  const ph = new Image();
  ph.onload = () => { placeholderReady.value = true; };
  ph.src = getPlaceholderUrl(props.backgroundImage);

  const full = new Image();
  full.onload = () => { fullReady.value = true; };
  full.src = props.backgroundImage;
});
</script>

<template>
  <div class="hero-section">
    <template v-if="backgroundImage">
      <div
        class="hero-bg hero-placeholder"
        :class="{ visible: placeholderReady }"
        :style="{ backgroundImage: `url(${getPlaceholderUrl(backgroundImage)})` }"
      />
      <div
        class="hero-bg hero-full"
        :class="{ visible: fullReady }"
        :style="{ backgroundImage: `url(${backgroundImage})` }"
      />
    </template>
    <div class="hero-overlay">
      <div class="hero-content">
        <h1 class="hero-title">{{ title }}</h1>
        <p v-if="subtitle" class="hero-subtitle">{{ subtitle }}</p>
      </div>
    </div>
  </div>
</template>

<style lang="sass" scoped>
.hero-section
  position: relative
  height: 60vh
  min-height: 400px
  display: flex
  align-items: center
  justify-content: center
  margin-bottom: 4rem
  overflow: hidden
  width: 100%

.hero-bg
  position: absolute
  inset: 0
  background-size: cover
  background-position: center
  opacity: 0
  transition: opacity 0.6s ease

  &.visible
    opacity: 1

.hero-placeholder
  filter: blur(8px)
  transform: scale(1.05)

.hero-overlay
  position: absolute
  inset: 0
  background: rgba(0, 0, 0, 0.35)
  display: flex
  align-items: center
  justify-content: center
  padding: 2rem

.hero-content
  text-align: center
  color: white
  max-width: 1100px
  z-index: 1

.hero-title
  font-size: clamp(3rem, 10vw, 5rem)
  font-weight: 800
  margin: 0
  letter-spacing: -1.5px
  line-height: 1.1
  text-transform: uppercase
  text-shadow: 0 4px 20px rgba(0, 0, 0, 0.4)

.hero-subtitle
  font-size: clamp(1.2rem, 3vw, 1.75rem)
  font-weight: 500
  opacity: 0.95
  margin: 1.5rem auto 0
  max-width: 800px
  line-height: 1.4
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.4)

@media (max-width: 768px)
  .hero-section
    height: 50vh
  .hero-title
    font-size: 2.5rem
  .hero-subtitle
    font-size: 1.1rem
</style>
